import type { OverviewResponse } from "contracts/api/dashboard/responses";
import { PILLAR } from "contracts/enums/metrics";
import { For, Show } from "solid-js";
import type { SummaryQuickLook } from "../../enums/scan";
import { SUMMARY_QUICK_LOOK } from "../../enums/scan";
import { healthVar } from "../../ui/health";
import { createTovStore } from "../website/tovStore";
import { coverageWord } from "./contentFitnessDimensionModel";
import {
  contentFitnessCoverageDetail,
  type PillarSignal,
  signalsForPillar,
} from "./scanModel";
import {
  fitnessEmptyCopy,
  HeroMetric,
  sourceCoverage,
  traceabilityStatus,
} from "./summaryModel";
export function ContentFitnessPreview(props: {
  data: OverviewResponse;
  openQuickLook: (kind: SummaryQuickLook) => void;
}) {
  const dimensions = () => signalsForPillar(props.data, PILLAR.CONTENT_FITNESS);
  return (
    <section class="signal-section">
      <div class="signal-section-head">
        <div>
          <h2>How the estate performs across Content Fitness</h2>
          <p>Open a detail view for rationale and evidence.</p>
        </div>
        <button
          class="signal-text-action"
          type="button"
          onClick={() => props.openQuickLook(SUMMARY_QUICK_LOOK.FITNESS)}
        >
          How scoring works
        </button>
      </div>
      <Show
        when={dimensions().length > 0}
        fallback={<p class="signal-muted">{fitnessEmptyCopy()}</p>}
      >
        <div class="signal-dimension-grid">
          <For each={dimensions()}>
            {(dimension) => (
              <DimensionCard
                dimension={dimension}
                websiteId={props.data.site.websiteId}
              />
            )}
          </For>
        </div>
      </Show>
    </section>
  );
}

function DimensionCard(props: { dimension: PillarSignal; websiteId: string }) {
  return (
    <a
      class="signal-dimension-card is-linked"
      href={`/w/${props.websiteId}/scan/content-fitness/${props.dimension.key}?from=summary`}
      aria-label={`Open ${props.dimension.label} detail`}
    >
      <div>
        <strong>{props.dimension.label}</strong>
        <span class="mono" style={{ color: healthVar(props.dimension.state) }}>
          {props.dimension.score}
        </span>
      </div>
      <div class="signal-dimension-bar" aria-hidden="true">
        <i
          style={{
            width: `${props.dimension.score}%`,
            background: healthVar(props.dimension.state),
          }}
        />
      </div>
      <Show when={props.dimension.coverageLabel || props.dimension.coverage}>
        <div class="signal-dimension-coverage">
          <Show when={props.dimension.coverageLabel}>
            {(label) => (
              <small class={`sig-state st-${props.dimension.coverageStatus}`}>
                {label()} / {coverageWord(props.dimension)}
              </small>
            )}
          </Show>
          <Show when={props.dimension.coverage}>
            {(coverage) => (
              <small>{contentFitnessCoverageDetail(coverage())}</small>
            )}
          </Show>
        </div>
      </Show>
      <p>{props.dimension.description}</p>
      <small class="signal-card-route">View detail</small>
    </a>
  );
}

export function BrandGuidelinesPreview(props: {
  data: OverviewResponse;
  websiteId: string;
  openQuickLook: (kind: SummaryQuickLook) => void;
}) {
  const tov = createTovStore(() => props.data.site.clientId);
  // A pending draft (no activation yet) is setup state, not active guidance.
  const version = () => (tov.hasActiveVersion() ? tov.version() : null);
  const sources = tov.sources;
  const inactiveLabel = () =>
    tov.versionId() && !tov.hasActiveVersion()
      ? "Setup in progress"
      : "Not available";
  return (
    <section class="signal-panel">
      <div class="signal-panel-head">
        <div>
          <span class="signal-mini-kicker">Assessment context</span>
          <h2>Brand Guidelines</h2>
          <p>
            Active guidance can inform applicable subcriteria across all five
            Content Fitness dimensions. It is not a separate score.
          </p>
        </div>
        <button
          class="signal-text-action"
          type="button"
          onClick={() =>
            props.openQuickLook(SUMMARY_QUICK_LOOK.BRAND_GUIDELINES)
          }
        >
          Quick look
        </button>
      </div>
      <div class="signal-scope-grid">
        <HeroMetric
          label="Active version"
          value={version()?.label ?? inactiveLabel()}
        />
        <HeroMetric
          label="Guideline coverage"
          value={sourceCoverage(sources() ?? undefined)}
        />
        <HeroMetric
          label="Stored rules"
          value={
            version() ? `${version()?.rules.length ?? 0}` : "Not available"
          }
        />
        <HeroMetric
          label="Rule evidence"
          value={traceabilityStatus(
            version() ?? undefined,
            sources() ?? undefined,
          )}
        />
      </div>
      <a
        class="signal-inline-link"
        href={`/w/${props.websiteId}/scan/brand-guidelines`}
      >
        Open Brand Guidelines
      </a>
    </section>
  );
}
